import { trpc } from './trpc'
import { inferRouterOutputs, inferRouterInputs } from '@trpc/server'
import { AppRouter } from '@tagadapay/trpc-types'
import { useMutation } from '@tanstack/react-query'
import { useToast } from '@/components/ui/use-toast'
import {
  processBulkSyncIntegrations,
  type BulkSyncStoreRef,
} from '@/app/stores/utils/bulk-sync-integrations'
import { useTRPCClient } from '@/lib/api/trpc-provider'

type OutputRouter = inferRouterOutputs<AppRouter>
type InputRouter = inferRouterInputs<AppRouter>

// Output types
export type ProductListOutput = OutputRouter['products']['list']
export type ProductItem = ProductListOutput['items'][number]
export type ProductDetails = OutputRouter['products']['getById']
export type ProductWithRelations = OutputRouter['products']['getDetails'][number]
export type SearchProductsOutput = OutputRouter['products']['search']
export type SearchProductOutputItem = SearchProductsOutput[number]
export type UpdateProductOutput = OutputRouter['products']['update']
export type DeleteProductsOutput = OutputRouter['products']['delete']
export type DeleteAllProductsOutput = OutputRouter['products']['deleteAllFromStore']
export type SyncShopifyOutput = OutputRouter['products']['syncShopify']
export type BulkUpdateAutoSyncOutput = OutputRouter['products']['bulkUpdateAutoSync']
export type SyncLimitsStatusOutput = OutputRouter['products']['getSyncLimitsStatus']
export type SyncDynamicSellingPlansOutput = OutputRouter['products']['syncDynamicSellingPlans']

// Input types
export type ListProductsInput = InputRouter['products']['list']
export type GetProductInput = InputRouter['products']['getById']
export type GetProductDetailsInput = InputRouter['products']['getDetails']
export type SearchProductInput = InputRouter['products']['search']
export type CreateProductInput = InputRouter['products']['create']
export type UpdateProductInput = InputRouter['products']['update']
export type DeleteProductsInput = InputRouter['products']['delete']
export type DeleteAllProductsInput = InputRouter['products']['deleteAllFromStore']
export type SyncShopifyInput = InputRouter['products']['syncShopify']
export type BulkUpdateAutoSyncInput = InputRouter['products']['bulkUpdateAutoSync']
export type SyncLimitsStatusInput = InputRouter['products']['getSyncLimitsStatus']
export type SyncDynamicSellingPlansInput = InputRouter['products']['syncDynamicSellingPlans']

// Query hooks
export const useProductsList = (params: ListProductsInput, options?: {
    refetchOnMount?: boolean | 'always';
    refetchOnWindowFocus?: boolean;
    enabled?: boolean;
    staleTime?: number;
    refetchInterval?: number | false;
}) => {
    return trpc.products.list.useQuery(params, options)
}

export const useProduct = (productId: string, enabled = true) => {
    return trpc.products.getById.useQuery(
        { productId },
        {
            enabled: enabled && !!productId,
            retry: 2,
            staleTime: 30000, // Consider data fresh for 30 seconds
        },
    )
}

export const useProductDetails = (productIds: string[], enabled = true) => {
    return trpc.products.getDetails.useQuery(
        { productIds },
        {
            enabled: enabled && productIds.length > 0,
        },
    )
}

export const useSearchProducts = (params: SearchProductInput, options?: { enabled?: boolean }) => {
    return trpc.products.search.useQuery(params, {
        enabled: options?.enabled !== false && !!params.storeId,
    })
}

export const useProductByVariantId = (variantId: string, enabled = true) => {
    return trpc.products.getByVariantId.useQuery(
        { variantId },
        {
            enabled: enabled && !!variantId,
            retry: 2,
            staleTime: 30000,
        },
    )
}

export const useProductByPriceId = (priceId: string, enabled = true) => {
    return trpc.products.getByPriceId.useQuery(
        { priceId },
        {
            enabled: enabled && !!priceId,
            retry: 2,
            staleTime: 30000,
        },
    )
}

export const useSyncLimitsStatus = (storeId: string, enabled = true) => {
    return trpc.products.getSyncLimitsStatus.useQuery(
        { storeId },
        {
            enabled: enabled && !!storeId,
            refetchInterval: 10000, // Refetch every 10 seconds to keep limits updated
            staleTime: 5000, // Consider data fresh for 5 seconds
        },
    )
}

// Mutation hooks
export const useCreateProduct = () => {
    const utils = trpc.useUtils()

    return trpc.products.create.useMutation({
        onSuccess: () => {
            utils.products.list.invalidate()
            utils.products.search.invalidate()
        },
        // Note: Global error handling is now handled in the TRPC provider
        // Individual components can still handle specific errors as needed
    })
}

export const useUpdateProduct = () => {
    const utils = trpc.useUtils()

    return trpc.products.update.useMutation({
        onSuccess: () => {
            utils.products.list.invalidate()
            utils.products.getById.invalidate()
            utils.products.getDetails.invalidate()
            utils.products.search.invalidate()
        },
    })
}

export const useDeleteProducts = () => {
    const utils = trpc.useUtils()

    return trpc.products.delete.useMutation({
        onSuccess: () => {
            utils.products.list.invalidate()
            utils.products.search.invalidate()
        },
    })
}

export const useDeleteAllStoreProducts = () => {
    const utils = trpc.useUtils()

    return trpc.products.deleteAllFromStore.useMutation({
        onSuccess: () => {
            utils.products.list.invalidate()
            utils.products.search.invalidate()
        },
    })
}

export const useSyncShopifyProducts = () => {
    const utils = trpc.useUtils()

    return trpc.products.syncShopify.useMutation({
        onSuccess: () => {
            utils.products.list.invalidate()
            utils.products.search.invalidate()
        },
    })
}

export const useWoocommerceSyncProducts = () => {
    const utils = trpc.useUtils()
    return trpc.products.syncWooCommerce.useMutation({
        onSuccess: () => {
            utils.products.list.invalidate()
            utils.products.search.invalidate()
        },
    })
}

export const useBulkUpdateProductAutoSync = () => {
    const utils = trpc.useUtils()

    return trpc.products.bulkUpdateAutoSync.useMutation({
        onSuccess: () => {
            utils.products.list.invalidate()
        },
    })
}


export const useSyncOneProduct = () => {
    const utils = trpc.useUtils()
    const { toast } = useToast()
    return trpc.products.syncOneProduct.useMutation({
        onSuccess: () => {
            utils.products.list.invalidate()
            utils.products.getDetails.invalidate()
            toast({
                title: 'Success',
                description: 'Product synced successfully',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            })
        }
    })
}

export const useSyncDynamicSellingPlans = () => {
    const utils = trpc.useUtils()

    return trpc.products.syncDynamicSellingPlans.useMutation({
        onSuccess: () => {
            utils.products.list.invalidate()
        },
    })
}

export const useSyncOneProductSellingPlans = () => {
    const utils = trpc.useUtils()
    const { toast } = useToast()
    return trpc.products.syncOneProductSellingPlans.useMutation({
        onSuccess: () => {
            utils.products.list.invalidate()
            utils.products.getDetails.invalidate()
            utils.products.getById.invalidate()
            toast({
                title: 'Success',
                description: 'Selling plans synced successfully',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            })
        },
    })
}

export function useBulkSyncStoreIntegrations() {
    const utils = trpc.useUtils()
    const client = useTRPCClient()
    return useMutation({
        mutationFn: async (input: { stores: BulkSyncStoreRef[] }) =>
            processBulkSyncIntegrations(input.stores, {
                syncShopify: (storeId) => client.products.syncShopify.mutate({ storeId }),
                syncWooCommerce: (storeId) => client.products.syncWooCommerce.mutate({ storeId }),
            }),
        onSuccess: () => {
            utils.products.list.invalidate()
            utils.products.getSyncLimitsStatus.invalidate()
        },
    })
}